import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { filterReducer } from '../../Reducers/filterReducer';
import { AllTShirtMeasuresMandatory } from '../../types/databaseTables/idealteemeasures';
import { NewTeeTypesRow } from '../../types/databaseTables/newteetypes';
import { TeeMeasuresAveragesRow } from '../../types/databaseTables/teemeasuresaverages';
import { FilterAction, FilterState } from '../../types/reducers/filterReducer';
import { getSecondsDifference } from '../../utils/timeFunctions';
import { GlobalContext } from '../GlobalContext/GlobalContextProvider';
import { LoggingContext } from '../LoggingContext/LoggingContext';
import { UserContext } from '../UserContext/UserContextProvider';
import { initialFitReducerState } from './searchContextFunctions';

export type FilterLabel = 'any' | 'styleTags' | 'updateResults';

interface SearchContextInterface {
  updateResults: () => void;
  filterState: FilterState;
  dispatchFilter: React.Dispatch<FilterAction>;
}

export const SearchContext = React.createContext<SearchContextInterface>(
  {} as SearchContextInterface
);

function getInitialReducerState(
  newTeeTypesRows: NewTeeTypesRow[],
  teeMeasuresAveragesRows: TeeMeasuresAveragesRow[],
  idealGarmentMeasurements: AllTShirtMeasuresMandatory,
  userFitScoreWeightings: AllTShirtMeasuresMandatory
) {
  return filterReducer(initialFitReducerState, {
    type: 'initialize filter state from fetch data or localStorage',
    newTeeTypesRows,
    teeMeasuresAveragesRows,
    idealGarmentMeasurements,
    userFitScoreWeightings,
  });
}

export default function SearchContextProvider(props: { children: React.ReactNode }) {
  const location = useLocation();
  // USECONTEXT
  const { addLog } = useContext(LoggingContext);
  const { filterMobileOpen, setFilterMobileOpen, timestampsState } =
    React.useContext(GlobalContext);
  const { userDetailsState } = useContext(UserContext);
  // USEREDUCER
  // in a useReducer(reducer, initialState) hook, the initialState is fired (if a function) on every update of the context. I've memoised to prevent the reducer firing on every update. I think I could have used the useReducer's third argument. See hooks docs.
  const memoisedInitial = React.useMemo(
    () =>
      getInitialReducerState(
        JSON.parse(localStorage.getItem('newTeeTypes') || '[]'),
        JSON.parse(localStorage.getItem('teeMeasuresAverages') || '[]'),
        userDetailsState.idealGarmentMeasurements,
        userDetailsState.fitScoreWeightings
      ),
    []
  );
  const [filterState, dispatchFilter] = React.useReducer(filterReducer, memoisedInitial);

  // Update the externalResults when user clicks UPDATE RESULTS button
  function updateResults(): void {
    addLog('Clicked Update Results Button', location.pathname);
    dispatchFilter({ type: 'update external' });
    if (filterMobileOpen) {
      setFilterMobileOpen(false);
    }
  }

  // On page/context load, update the initial filterState
  useEffect(() => {
    // Only update if the page has just loaded
    const secondsSinceLastUpdate = getSecondsDifference(
      userDetailsState.timestamps.idealGarmentMeasurements,
      new Date().toUTCString()
    );
    if (secondsSinceLastUpdate > 10) return;
    // Update filter state with recently updated idealMeasures
    dispatchFilter({
      type: 'initialize filter state from fetch data or localStorage',
      newTeeTypesRows: JSON.parse(localStorage.getItem('newTeeTypes') || '[]'),
      teeMeasuresAveragesRows: JSON.parse(localStorage.getItem('teeMeasuresAverages') || '[]'),
      idealGarmentMeasurements: userDetailsState.idealGarmentMeasurements,
      userFitScoreWeightings: userDetailsState.fitScoreWeightings,
    });
  }, [userDetailsState.timestamps.idealGarmentMeasurements]);

  // Update slider filter min/max if ideal measures change
  useEffect(() => {
    // Only update if idealMeasures have been updated just now. Prevent update if we're just visiting page for first time
    const secondsSinceLastUpdate = getSecondsDifference(
      userDetailsState.timestamps.idealGarmentMeasurements,
      new Date().toUTCString()
    );
    if (secondsSinceLastUpdate > 10) return;
    // Update filter state with recently updated idealMeasures
    dispatchFilter({
      type: 'initialize filter state from fetch data or localStorage',
      newTeeTypesRows: JSON.parse(localStorage.getItem('newTeeTypes') || '[]'),
      teeMeasuresAveragesRows: JSON.parse(localStorage.getItem('teeMeasuresAverages') || '[]'),
      idealGarmentMeasurements: userDetailsState.idealGarmentMeasurements,
      userFitScoreWeightings: userDetailsState.fitScoreWeightings,
    });
  }, [userDetailsState.timestamps.idealGarmentMeasurements]);

  // Initialise filterState when newTeeTypes are fetched from back-end (or on start-up)
  useEffect(() => {
    // Only update if fetch has happened just now. Prevent update if we're just visiting page for first time
    const secondsSinceLastUpdate = getSecondsDifference(
      timestampsState.fetch.newTeeTypes,
      new Date().toUTCString()
    );
    if (secondsSinceLastUpdate > 10) return;
    dispatchFilter({
      type: 'initialize filter state from fetch data or localStorage',
      newTeeTypesRows: JSON.parse(localStorage.getItem('newTeeTypes') || '[]'),
      teeMeasuresAveragesRows: JSON.parse(localStorage.getItem('teeMeasuresAverages') || '[]'),
      idealGarmentMeasurements: userDetailsState.idealGarmentMeasurements,
      userFitScoreWeightings: userDetailsState.fitScoreWeightings,
    });
  }, [timestampsState.fetch.newTeeTypes]);

  const value = React.useMemo(
    () => ({
      updateResults,
      filterState,
      dispatchFilter,
    }),
    [filterState]
  );

  return <SearchContext.Provider value={value} {...props} />;
}
